import { MONTH_NAMES } from "../../constants/common/common";
import { getOrdinalSuffix } from "../common/deliveryDate";
import { plpPageConstants } from "../../constants/pageConstants/plpConstants";
import isMobile from "../common";
import { getLastVisitedAccuracyLevel } from "../checkout/deliveryAddress";

/**
 * @typedef {'instant' | 'today' | 'morning' | 'tomorrow' | 'future' | 'legacy'} DeliveryDisplayType
 * @typedef {{ type: DeliveryDisplayType, primaryIcon: string|null, secondaryIcon: string|null, text: string, primaryIconWidth: number, primaryIconHeight: number, secondaryIconWidth: number, secondaryIconHeight: number }} ProductDeliveryDisplay
 */

/**
 * Parses a serviceability date string (DD-MM-YY) into a local Date at midnight.
 *
 * @param {string} dateStr
 * @returns {Date|null}
 */
export const parseServiceabilityDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") {
    return null;
  }

  const parts = dateStr.trim().split("-");

  if (parts.length !== 3) {
    return null;
  }

  const day = Number.parseInt(parts[0], 10);
  const month = Number.parseInt(parts[1], 10) - 1;
  let year = Number.parseInt(parts[2], 10);

  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) {
    return null;
  }

  if (year < 100) {
    year += 2000;
  }

  const date = new Date(year, month, day);
  date.setHours(0, 0, 0, 0);

  return date;
};

/**
 * Compares a serviceability date to today and tomorrow.
 *
 * @param {string} dateStr
 * @returns {'today'|'tomorrow'|'future'|null}
 */
export const getServiceabilityDateRelation = (dateStr) => {
  const date = parseServiceabilityDate(dateStr);

  if (!date) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.getTime() === today.getTime()) {
    return "today";
  }

  if (date.getTime() === tomorrow.getTime()) {
    return "tomorrow";
  }

  return "future";
};

/**
 * Formats a future serviceability date as "20ᵗʰ Jul" (ordinal suffix as superscript).
 *
 * @param {string} dateStr
 * @returns {string}
 */
export const formatFutureServiceabilityDate = (dateStr) => {
  const parts = dateStr?.trim().split("-");

  if (!parts || parts.length !== 3) {
    return dateStr || "";
  }

  const day = Number.parseInt(parts[0], 10);
  const month = Number.parseInt(parts[1], 10);

  if (Number.isNaN(day) || Number.isNaN(month) || month < 1 || month > 12) {
    return dateStr;
  }

  const monthName = MONTH_NAMES[month - 1]?.slice(0, 3);

  return `${getOrdinalSuffix(day)} ${monthName}`;
};

/**
 * Returns true when a delivery time string is on or before 10:00 AM.
 * Supports API formats like "10 AM", "9 AM", "9:00 AM", and 24-hour "09:30".
 *
 * @param {string} timeStr
 * @returns {boolean}
 */
export const isDeliveryTimeBefore10AM = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") {
    return false;
  }

  const normalized = timeStr.trim().toUpperCase();
  const twelveHourMatch = normalized.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);

  if (twelveHourMatch) {
    const hours = Number.parseInt(twelveHourMatch[1], 10);
    const minutes = twelveHourMatch[2] ? Number.parseInt(twelveHourMatch[2], 10) : 0;
    const period = twelveHourMatch[3];

    if (period === "PM") {
      return false;
    }

    if (hours === 12) {
      return true;
    }

    if (hours < 10) {
      return true;
    }

    return hours === 10 && minutes === 0;
  }

  const twentyFourHourMatch = normalized.match(/^(\d{1,2}):(\d{2})$/);

  if (twentyFourHourMatch) {
    const hours = Number.parseInt(twentyFourHourMatch[1], 10);
    const minutes = Number.parseInt(twentyFourHourMatch[2], 10);

    return hours < 10 || (hours === 10 && minutes === 0);
  }

  return false;
};

// CleverTap A/B variable `deliveryTimeVisibility` (via useABTestingView): where the delivery time is shown.
// current: PLP + PDP (existing experience), none: neither, single: PDP only. Empty / missing / unknown -> current.
// Runs only for a medium / low accuracy (or pincode) gate location; a high accuracy gate location always gets
// the current experience.
export const DELIVERY_TIME_VISIBILITY = { CURRENT: "current", NONE: "none", SINGLE: "single" };

/**
 * @returns {boolean} The location picked at the gate is high accuracy (A/B experiment does not apply).
 */
const isHighAccuracyGateLocation = () => getLastVisitedAccuracyLevel() === "high";

/**
 * @param {string} [variant] - deliveryTimeVisibility value.
 * @returns {string} Normalised variant; an empty value (e.g. "", "  ", null) is "current".
 */
const normaliseDeliveryTimeVariant = (variant) =>
  String(variant ?? "")
    .trim()
    .toLowerCase() || DELIVERY_TIME_VISIBILITY.CURRENT;

/**
 * PLP card delivery tag shows day/date only (no Now / Morning / time) for the "none" and "single" variants.
 * @param {string} [variant] - deliveryTimeVisibility value.
 * @returns {boolean}
 */
export const isPlpDeliveryTimeHidden = (variant) =>
  !isHighAccuracyGateLocation() &&
  [DELIVERY_TIME_VISIBILITY.NONE, DELIVERY_TIME_VISIBILITY.SINGLE].includes(normaliseDeliveryTimeVariant(variant));

/**
 * PDP nudge below the delivery date hides the delivery time for the "none" variant.
 * @param {string} [variant] - deliveryTimeVisibility value.
 * @returns {boolean}
 */
export const isPdpDeliveryTimeHidden = (variant) =>
  !isHighAccuracyGateLocation() && normaliseDeliveryTimeVariant(variant) === DELIVERY_TIME_VISIBILITY.NONE;

/**
 * Same Day tag only (no clock / time), used when the delivery time is hidden.
 * @returns {ProductDeliveryDisplay}
 */
const getSameDayDateOnlyDisplay = () => ({
  type: "today",
  primaryIcon: plpPageConstants.SAME_DAY_PLP,
  secondaryIcon: null,
  text: "",
  primaryIconWidth: isMobile() ? 75 : 85,
  primaryIconHeight: 18,
  secondaryIconWidth: 0,
  secondaryIconHeight: 0,
});

/**
 * Tomorrow tag without a time ("Earliest Delivery: Tomorrow").
 * @returns {ProductDeliveryDisplay}
 */
const getTomorrowDateOnlyDisplay = () => ({
  type: "tomorrow",
  primaryIcon: plpPageConstants.DELIVERY_BIKE_ICON,
  secondaryIcon: null,
  text: `${plpPageConstants.EARLIEST_DELIVERY}: Tomorrow`,
  primaryIconWidth: 16,
  primaryIconHeight: 16,
  secondaryIconWidth: 0,
  secondaryIconHeight: 0,
});

/**
 * Builds the delivery row display config from a serviceability entry.
 *
 * @param {string|Object|null|undefined} entry
 * @param {Object} [options]
 * @param {boolean} [options.isTimeHidden] - Day/date only (deliveryTimeVisibility A/B): Now -> Same Day,
 *   Morning -> Tomorrow, no "By <time>"; future dates and legacy text are unchanged.
 * @returns {ProductDeliveryDisplay|null}
 */
export const getProductDeliveryDisplay = (entry, { isTimeHidden = false } = {}) => {
  if (!entry) {
    return null;
  }

  if (typeof entry === "string") {
    const deliveryText = entry.trim();

    if (!deliveryText) {
      return null;
    }

    return {
      type: "legacy",
      primaryIcon: null,
      secondaryIcon: null,
      text: deliveryText,
      primaryIconWidth: 0,
      primaryIconHeight: 0,
      secondaryIconWidth: 0,
      secondaryIconHeight: 0,
    };
  }

  const instantDelivery = entry?.instantDelivery?.trim();

  if (instantDelivery) {
    if (isTimeHidden) return getSameDayDateOnlyDisplay();
    return {
      type: "instant",
      primaryIcon: plpPageConstants.FNP_NOW_ICON,
      secondaryIcon: plpPageConstants.DELIVERY_CLOCK_ICON,
      text: `${instantDelivery} mins`,
      primaryIconWidth: isMobile() ? 40 : 48,
      primaryIconHeight: 9,
      secondaryIconWidth: 16,
      secondaryIconHeight: 16,
    };
  }

  const earliestDelivery = entry?.earliestDelivery?.trim();
  const time = entry?.time?.trim();

  if (!earliestDelivery) {
    return null;
  }

  const relation = getServiceabilityDateRelation(earliestDelivery);

  if (relation === "today") {
    if (isTimeHidden) return getSameDayDateOnlyDisplay();
    return {
      type: "today",
      primaryIcon: plpPageConstants.SAME_DAY_PLP,
      secondaryIcon: plpPageConstants.DELIVERY_CLOCK_ICON,
      text: time ? `By ${time}` : "",
      primaryIconWidth: isMobile() ? 75 : 85,
      primaryIconHeight: 18,
      secondaryIconWidth: 16,
      secondaryIconHeight: 16,
    };
  }

  if (relation === "tomorrow") {
    // Morning is a tomorrow (by 10 AM) delivery, so day/date only shows it as Tomorrow.
    if (isTimeHidden) return getTomorrowDateOnlyDisplay();
    const isMorningDelivery = Boolean(time) && isDeliveryTimeBefore10AM(time);

    if (isMorningDelivery) {
      return {
        type: "morning",
        primaryIcon: plpPageConstants.MORNING_DELIVERY_ICON,
        secondaryIcon: plpPageConstants.DELIVERY_CLOCK_ICON,
        text: `By ${time}`,
        primaryIconWidth: isMobile() ? 75 : 85,
        primaryIconHeight: 18,
        secondaryIconWidth: 16,
        secondaryIconHeight: 16,
      };
    }

    return {
      type: "tomorrow",
      primaryIcon: plpPageConstants.DELIVERY_BIKE_ICON,
      secondaryIcon: null,
      text: time ? `Tomorrow by ${time}` : `${plpPageConstants.EARLIEST_DELIVERY}: Tomorrow`,
      primaryIconWidth: 16,
      primaryIconHeight: 16,
      secondaryIconWidth: 0,
      secondaryIconHeight: 0,
    };
  }

  return {
    type: "future",
    primaryIcon: plpPageConstants.DELIVERY_BIKE_ICON,
    secondaryIcon: null,
    text: `${plpPageConstants.EARLIEST_DELIVERY}: ${formatFutureServiceabilityDate(earliestDelivery)}`,
    primaryIconWidth: 16,
    primaryIconHeight: 16,
    secondaryIconWidth: 0,
    secondaryIconHeight: 0,
  };
};

/**
 * Maps a serviceability entry to CDP impression delivery fields.
 * deliveryType: "fnp-now" | "today" | "tomorrow" | "future" | ""
 * deliveryTime: minutes for fnp-now, clock time for today/tomorrow, formatted date for future
 *
 * @param {string|Object|null|undefined} entry
 * @returns {{ deliveryType: string, deliveryTime: string }}
 */
export const getImpressionDeliveryMeta = (entry) => {
  const emptyMeta = { deliveryType: "", deliveryTime: "" };

  if (!entry || typeof entry === "string") {
    return emptyMeta;
  }

  const instantDelivery = entry?.instantDelivery?.trim();

  if (instantDelivery) {
    return {
      deliveryType: "now",
      deliveryTime: instantDelivery,
    };
  }

  const earliestDelivery = entry?.earliestDelivery?.trim();
  const time = entry?.time?.trim() || "";

  if (!earliestDelivery) {
    return emptyMeta;
  }

  const relation = getServiceabilityDateRelation(earliestDelivery);

  if (relation === "today") {
    return { deliveryType: "today", deliveryTime: time };
  }

  if (relation === "tomorrow") {
    return { deliveryType: "tomorrow", deliveryTime: time };
  }

  if (relation === "future") {
    return {
      deliveryType: "future",
      deliveryTime: formatFutureServiceabilityDate(earliestDelivery),
    };
  }

  return emptyMeta;
};

/**
 * Extracts earliest delivery text from a serviceability entry or legacy string value.
 *
 * @param {string|Object} entry
 * @returns {string}
 */
export const getEarliestDeliveryText = (entry) => {
  const display = getProductDeliveryDisplay(entry);

  if (!display?.text) {
    return "";
  }

  if (display.type === "today") {
    return display.text ? `Today ${display.text}` : "Today";
  }

  if (display.type === "morning") {
    return display.text ? `Tomorrow ${display.text}` : "Tomorrow";
  }

  return display.text;
};
